import cn from 'classnames';
import type { FC, ReactNode } from 'react';
import { memo, useCallback, useMemo } from 'react';

import { Layout } from '@sravni/react-design-system/lib/Layout';
import { useIsMobile } from '@sravni/react-utils';

import type { CommonCardProps } from '@src/@types/commonCardProps';
import { usePageContext } from '@src/config/PageContext';
import { BANNER_LIST_POSITION_INDEX, BANNER_TYPE } from '@src/constants/banners';
import { getBanners } from '@src/helpers/banners';
import type { IFilter } from '@src/hooks/useFilters';
import { useSelectBanners } from '@src/reducers/banners';
import { useIsHiddenPskCard } from '@src/reducers/metadata';
import { useSelectCreditsList } from '@src/reducers/microcredits';
import { useIsPromoActive } from '@src/reducers/promo';
import { NoContentPlaceholder } from 'shared/ui/NoContentPlaceholder';
import { LightweightCard } from 'widgets/LightweightCard';
import { DEFAULT_CONTENT } from 'widgets/LightweightCard/ui/LightweightCard.config';

import type { LightweightCardContentSettings } from '../LightweightCard/types';

import Preloader from './components/Preloader';
import styles from './styles.module.scss';
import { errorText, getEmptyProductsTexts } from './utils';

interface IProps {
  className?: string;
  preloadTotal?: number;
  filter: IFilter;

  gridLayout?: boolean;
  renderCard?: (props: CommonCardProps) => ReactNode;
}

export const ProductListCommon: FC<IProps> = memo((props: IProps) => {
  const { preloadTotal = 16, gridLayout = true, renderCard = (props) => <LightweightCard {...props} /> } = props;

  const isMobile = useIsMobile();
  const isPromoActive = useIsPromoActive();

  const { withBanners, listKey, withCardDetailsClick } = usePageContext();
  const banners = useSelectBanners();
  const { error, items = [], loading, loadingMore } = useSelectCreditsList(listKey);

  const renderSlot = useCallback(
    (index, listPosition = BANNER_LIST_POSITION_INDEX) => withBanners && getBanners({ index, banners, listPosition }),
    [withBanners, banners],
  );

  const isHiddenPsk = useIsHiddenPskCard();

  const defaultSettings = useMemo(() => {
    const content: LightweightCardContentSettings = {
      ...DEFAULT_CONTENT,
      psk: { visible: !isHiddenPsk },
    };

    return content;
  }, [isHiddenPsk]);

  const renderListContent = useCallback(
    () =>
      items.reduce((output, item, index) => {
        const slot = renderSlot(index, BANNER_LIST_POSITION_INDEX);
        if (slot) {
          output.push(
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`${banners[BANNER_TYPE].id}-${index}`}
              className={styles.banner}
            >
              {slot}
            </div>,
          );
        }

        output.push(
          renderCard({
            key: `p-item-${index}`,
            offer: item,
            position: index,
            contentSettings: defaultSettings,
            meta: {
              isMobile,
              isPromoActive,
              withCardDetailsClick,
            },
          }),
        );

        return output;
      }, []),
    [items, renderSlot, renderCard, defaultSettings, isMobile, isPromoActive, withCardDetailsClick, banners],
  );

  if (error) {
    return <NoContentPlaceholder {...errorText} />;
  }

  if (!items.length && !loading) {
    const { message, title } = getEmptyProductsTexts(listKey);

    return (
      <div className={styles.container}>
        <Layout className={styles.layout}>
          <NoContentPlaceholder message={message} title={title} />
        </Layout>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <Layout className={styles.layout}>
          <Preloader className={cn(styles.list, styles.gridLayout)} itemsCount={preloadTotal} gridLayout={gridLayout} />
        </Layout>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Layout className={styles.layout}>
        <div className={cn(styles.list, styles.gridLayout)}>{renderListContent()}</div>
        {loadingMore && (
          <Preloader className={cn(styles.list, styles.gridLayout)} itemsCount={preloadTotal} gridLayout={gridLayout} />
        )}
      </Layout>
    </div>
  );
});
